/**
 * bt-adapter — transport ESC/POS via le bridge Bluetooth Capacitor (SPP).
 *
 * Extrait de printer-adapters.ts (cliquet max-lines). Connexion gardée chaude
 * entre impressions + envois sérialisés par imprimante (bt-send-queue).
 */

import { enqueueSend } from './bt-send-queue';

// Connexion BT gardée chaude entre impressions (2026-06-16). Avant :
// connect→write→flush 1.5-5s→disconnect par ticket. Le bridge n'a pas
// isConnected() → on suit le mac connecté ici ; reconnexion+retry si write KO.
let btConnectedMac: string | null = null;

export type BtSendOptions = {
  /**
   * Fermer la connexion après l'envoi. Les imprimantes cuisine Sunmi Cloud
   * (SPP) n'impriment la fin du document et ne coupent qu'à la fermeture de
   * la socket : avec la connexion « chaude » (juin 2026) le bon s'arrêtait
   * trop tôt et ne coupait plus (retour 2026-09-10, « ça coupait avant »).
   * Le disconnect natif attend 350 ms que le firmware vide son tampon.
   */
  closeAfter?: boolean;
};

export function sendViaBluetooth(
  mac: string,
  data: Uint8Array,
  opts: BtSendOptions = {},
): Promise<boolean> {
  // Un seul envoi à la fois par imprimante : les bons par service (N KOT
  // simultanés) se chevauchaient sur le socket SPP → bon tronqué, coupe perdue.
  return enqueueSend(mac, () => sendViaBluetoothNow(mac, data, opts));
}

async function sendViaBluetoothNow(
  mac: string,
  data: Uint8Array,
  opts: BtSendOptions,
): Promise<boolean> {
  const { getBluetoothBridge } = await import('@/services/hardware/printer/bt/BluetoothBridge');
  const bridge = await getBluetoothBridge();
  if (!bridge) throw new Error('Bridge Bluetooth non disponible');
  if (btConnectedMac !== mac) {
    await bridge.connect(mac);
    btConnectedMac = mac;
  }
  try {
    await bridge.write(mac, data);
  } catch {
    btConnectedMac = null; // connexion tombée → reconnexion + retry unique
    await bridge.connect(mac);
    btConnectedMac = mac;
    await bridge.write(mac, data);
  }
  const flushMs = Math.min(1500, Math.max(300, Math.round(data.length * 0.3)));
  await new Promise((r) => setTimeout(r, flushMs));
  if (opts.closeAfter) {
    // Fin de document explicite : le firmware imprime le reste et coupe.
    try {
      await bridge.disconnect(mac);
    } catch {
      /* déjà fermée */
    }
    btConnectedMac = null;
  }
  return true;
}
